import { TokenListItem } from '../TokenListItem'
import { EmptyCard } from '../EmptyCard'
import * as Collapsible from '@radix-ui/react-collapsible'
import { Typography } from '@/ui/atoms/typography/Typography'
import { ChevronDownIcon } from 'lucide-react'
import { cn } from '@/ui/utils/style'
import { PropsWithChildren } from 'react'
import { Info } from '@/ui/molecules/info/Info'
import { useSideDrawer } from '@/domain/hooks/useSideDrawer'
import { TokenData } from '../../logic/types'

export function TokensTab({ supported, otherTokens, totalUSD }: TokenData) {
  const { tokensTab } = useSideDrawer()
  const { isSupportedOpen, isOtherOpen, setIsSupportedOpen, setIsOtherOpen } = tokensTab
  const hasSupportedTokens = supported.evm.length > 0 || supported.l1.length > 0
  const hasOtherTokens = otherTokens.evm.length > 0 || otherTokens.l1.length > 0

  if (!hasSupportedTokens && !hasOtherTokens) {
    return <EmptyCard header="No tokens found" />
  }

  return (
    <div>
      {hasSupportedTokens && (
        <Collapsible.Root className="mb-6" open={isSupportedOpen} onOpenChange={setIsSupportedOpen}>
          <Collapsible.Trigger className="w-full">
            <SectionHeading isOpen={isSupportedOpen}>Supported tokens ({totalUSD})</SectionHeading>
          </Collapsible.Trigger>
          <Collapsible.Content className="pl-6">
            {supported.evm.map(({ token, balance }) => (
              <TokenListItem key={token.address} token={token} value={balance} />
            ))}

            {supported.l1.map(({ token, balance }) => (
              <TokenListItem key={token.address} token={token} value={balance} isL1 />
            ))}
          </Collapsible.Content>
        </Collapsible.Root>
      )}

      {hasOtherTokens && (
        <Collapsible.Root className="mb-6" open={isOtherOpen} onOpenChange={setIsOtherOpen}>
          <Collapsible.Trigger className="w-full">
            <SectionHeading isOpen={isOtherOpen} infoText="Tokens outside of HypurrFi's supported list.">
              Other tokens ({otherTokens.evm.length + otherTokens.l1.length})
            </SectionHeading>
          </Collapsible.Trigger>
          <Collapsible.Content className="pl-6">
            {otherTokens.evm.map(({ token, balance }) => (
              <TokenListItem key={token.address} token={token} value={balance} />
            ))}

            {otherTokens.l1.map(({ token, balance }) => (
              <TokenListItem key={token.address} token={token} value={balance} isL1 />
            ))}
          </Collapsible.Content>
        </Collapsible.Root>
      )}
    </div>
  )
}

function SectionHeading({ isOpen, infoText, children }: PropsWithChildren<{ isOpen: boolean; infoText?: string }>) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <div className="flex items-center gap-1">
        <Typography variant="prompt" className="flex items-center justify-between gap-2 text-sm">
          {children}
        </Typography>

        {infoText && <Info>{infoText}</Info>}
      </div>

      <div className="flex items-center gap-2">
        <ChevronDownIcon
          className={cn('h-4 w-4 text-prompt-foreground transition-transform', isOpen && 'rotate-180')}
        />
      </div>
    </div>
  )
}
